import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const areas = [
  {
    num: '01',
    icon: 'ri-heart-pulse-line',
    title: 'Medical Equipment Supply',
    titleSub: 'Global Healthcare Solutions',
    color: '#0F7A5A',
    bg: '#F0FAF5',
    partner: 'Karl Group Holdings Corporation (Philippines)',
    partnerUrl: 'karlgroup.org',
    partnerDesc: 'Karl Group was founded in Zamboanga, Philippines and has expanded its operations throughout Mindanao, Manila, the Visayas, and Luzon. As the leading Philippine healthcare group, it holds a rich portfolio spanning medical equipment distribution, simulation training, hospital technology management, and medical software development.',
    summary: 'As the official Korean partner of Karl Group, Fixcope stably supplies high-quality Korean medical equipment and related products to the Philippine market. The Philippines relies on imports for over 99% of its medical devices, creating strong demand for Korean-made medical equipment.',
    items: [
      'Hospital Diagnostic Equipment',
      'Medical Imaging Devices — Ultrasound, Endoscopy, and more',
      'Medical Consumables & Related Products',
      'Medical Simulation & Training Equipment',
    ],
    highlights: [
      { label: 'Market Scale', value: '1,915+ hospitals & medical facilities nationwide' },
      { label: 'Import Dependency', value: 'Over 99% of medical devices are imported' },
      { label: 'Growth Potential', value: '6,000+ public health facilities by 2028' },
      { label: 'Partnership', value: 'Official Korean Partner of Karl Group' },
    ],
    image: 'https://public.readdy.ai/ai/img_res/5f6feff3cc3f1e77bee52fab2d68e530.jpg',
  },
  {
    num: '02',
    icon: 'ri-eye-line',
    title: 'Machine Vision Inspection',
    titleSub: 'Vision Inspection Solutions',
    color: '#0F7A5A',
    bg: '#F0FAF5',
    partner: 'Inspiraz Technology Pte Ltd (Singapore)',
    partnerUrl: 'vizcam.net / inspiraz.com',
    partnerDesc: 'Inspiraz Technology was founded in Singapore in 2004 and is a specialist in machine vision and automation. With over 20 years of expertise, the company delivers advanced solutions including vision inspection, AI deep learning, 3D vision, and thermal management to manufacturing clients across Asia.',
    summary: 'Wizer is a machine vision software platform developed by Inspiraz. With an intuitive drag-and-drop interface, users can build diverse vision inspection solutions quickly — no programming knowledge required.',
    items: [
      'Code Recognition & Tracking (Barcode / QR Code Reading)',
      'Optical Character Recognition — OCR',
      'Dimensional Measurement & Precision Inspection',
      'Surface Defect Detection',
      'Robot Guidance & Alignment',
      'Color Inspection',
    ],
    highlights: [
      { label: 'Wizer Pro', value: 'High-performance general-purpose platform' },
      { label: 'Wizer RTU', value: '20+ pre-configured modules, ready to use' },
      { label: 'Wizer Intuit', value: 'AI self-learning — defect detection from small image sets' },
      { label: 'Wizer 3D', value: '3D inspection solution deployable within 1 hour' },
    ],
    image: 'https://readdy.ai/api/search-image?query=Industrial%20machine%20vision%20inspection%20system%20automated%20manufacturing%20quality%20control%20camera%20scanning%20production%20line%20precision%20technology%20green%20tones%20elegant%20dark%20background%20high%20tech%20industrial%20automation&width=800&height=500&seq=fixcope-vision-001&orientation=landscape',
  },
  {
    num: '03',
    icon: 'ri-camera-3-line',
    title: 'Micro Detachable-Head Camera',
    titleSub: 'Proprietary Camera Technology',
    color: '#0F7A5A',
    bg: '#F0FAF5',
    partner: 'Fixcope',
    partnerUrl: '',
    partnerDesc: 'Fixcope develops ultra-compact detachable-head cameras capable of operating in narrow spaces and special environments where conventional cameras cannot reach.',
    summary: 'Designed with a separated camera head and body, this system captures high-quality footage even in the tightest spaces. Built on Fixcope\'s proprietary technology, it is optimized for the specialized requirements of industrial environments.',
    items: [
      'Industrial Endoscopic Inspection — piping, engines, structures',
      'Defense & Aerospace',
      'Research & Science',
      'Construction & Plant Facility Inspection',
    ],
    highlights: [
      { label: 'Detachable Head', value: 'Ultra-compact 2.4 x 2.4mm head size' },
      { label: 'High-quality Imaging', value: 'Clear footage even in extreme environments' },
      { label: 'Durability', value: 'Optimized for industrial & medical field conditions' },
      { label: 'Long-range Transmission', value: 'Video transmission up to 15M' },
    ],
    image: 'https://static.readdy.ai/image/e26dc5fd8ad5af2fe8b73f60c590f9ca/345c1d41912f84687c667cc753c92727.png',
  },
  {
    num: '04',
    icon: 'ri-global-line',
    title: 'Global Business Consulting',
    titleSub: 'Asia · Africa Market Entry',
    color: '#0F7A5A',
    bg: '#F0FAF5',
    partner: 'Asia · Africa Network',
    partnerUrl: '',
    partnerDesc: 'Fixcope offers specialist consulting services for Korean companies entering overseas markets, backed by a robust global network and extensive project experience spanning diverse industries.',
    summary: 'We go beyond information — we accompany you through the entire process, from partner discovery and negotiations to contract closing and project execution. Fixcope is the ideal partner for manufacturers and tech companies targeting Asia and Africa.',
    items: [
      'Market Entry Strategy — target market analysis, regulatory & licensing guidance',
      'Partner Discovery & Negotiation — local trusted partner matching, MOU support',
      'Project Management — from planning to delivery & after-sales support',
      'Global Network Access — Asia · Africa regional connections',
    ],
    highlights: [
      { label: 'Medical Device Firms', value: 'Philippines & Southeast Asia market entry support' },
      { label: 'Manufacturers', value: 'Overseas industrial automation & vision inspection projects' },
      { label: 'Startups', value: 'Global partnership building support' },
      { label: 'Mid-sized Enterprises', value: 'Immediate access to verified local networks' },
    ],
    image: 'https://readdy.ai/api/search-image?query=Global%20business%20consulting%20world%20map%20connections%20Southeast%20Asia%20Philippines%20Singapore%20network%20professional%20meeting%20room%20executive%20elegant%20dark%20atmosphere%20with%20glowing%20connection%20lines%20representing%20international%20partnerships%20green%20accents%20modern%20corporate%20setting&width=800&height=500&seq=fixcope-consulting-001&orientation=landscape',
  },
];

export default function BusinessAreasEn() {
  const [active, setActive] = useState(0);
  const headerAnim = useScrollAnimation();
  const contentAnim = useScrollAnimation({ rootMargin: '0px 0px -40px 0px' });

  const area = areas[active];

  return (
    <section id="business" className="py-28 bg-[#FFFBF5]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          ref={headerAnim.ref}
          className={`text-center mb-16 transition-all duration-700 ${headerAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="w-8 h-[2px] bg-[#E8650A]"></span>
            <span className="text-xs font-semibold text-[#E8650A] uppercase tracking-widest whitespace-nowrap">Business Areas</span>
            <span className="w-8 h-[2px] bg-[#E8650A]"></span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1A2530] mb-4">4 Core Business Divisions</h2>
          <p className="text-gray-500 text-[15px] mx-auto whitespace-nowrap">
            Fixcope&apos;s specialized business domains — merging technology with a global network.
          </p>
        </div>

        {/* Tab Buttons */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-12">
          {areas.map((a, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                active === i
                  ? 'bg-[#E8650A] text-white shadow-sm'
                  : 'bg-white text-gray-500 border border-gray-200 hover:border-[#E8650A] hover:text-[#E8650A]'
              }`}
            >
              <span className="text-xs font-bold opacity-60">{a.num}</span>
              <span className="text-center leading-tight text-xs sm:text-sm">{a.title}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div
          ref={contentAnim.ref}
          key={active}
          className={`transition-all duration-500 ${contentAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: Image + Partner */}
            <div className="space-y-5">
              <div className="rounded-2xl overflow-hidden h-64 md:h-72">
                <img
                  src={area.image}
                  alt={area.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Partner Card */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#E8650A]/10 shrink-0">
                    <i className="ri-building-2-line text-lg text-[#E8650A]"></i>
                  </div>
                  <div>
                    <div className="text-xs text-[#E8650A] font-semibold mb-1 uppercase tracking-wide whitespace-nowrap">Partner</div>
                    <h4 className="text-sm font-bold text-[#1A2530] mb-1">{area.partner}</h4>
                    {area.partnerUrl && (
                      <p className="text-xs text-gray-400 mb-2">{area.partnerUrl}</p>
                    )}
                    <p className="text-xs text-gray-500 leading-relaxed">{area.partnerDesc}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Details */}
            <div className="space-y-5">
              {/* Title */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#E8650A]/10">
                    <i className={`${area.icon} text-xl text-[#E8650A]`}></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#1A2530]">{area.title}</h3>
                    <p className="text-xs text-gray-400">{area.titleSub}</p>
                  </div>
                </div>
                <p className="text-[14px] text-gray-600 leading-relaxed">{area.summary}</p>
              </div>

              {/* Apply Areas */}
              <div className="bg-white rounded-2xl p-5 border border-gray-100">
                <h5 className="text-xs font-bold text-[#1A2530] uppercase tracking-wider mb-4 whitespace-nowrap">Key Areas</h5>
                <ul className="space-y-2">
                  {area.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-gray-600">
                      <i className="ri-checkbox-circle-line text-[#E8650A] text-base shrink-0 mt-0.5"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Highlights Grid */}
              <div className="grid grid-cols-2 gap-3">
                {area.highlights.map((h, i) => (
                  <div key={i} className="bg-[#FEF3E8] rounded-xl p-4 border border-[#FDDDB4]">
                    <div className="text-[10px] text-[#E8650A] font-bold uppercase tracking-wide mb-1 whitespace-nowrap">{h.label}</div>
                    <div className="text-xs text-[#1A2530] font-medium leading-snug">{h.value}</div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <button
                onClick={() => {
                  const el = document.querySelector('#contact');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex items-center gap-2 text-sm font-semibold text-[#E8650A] hover:gap-3 transition-all whitespace-nowrap cursor-pointer"
              >
                Inquire about this area <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
